// Instant client-side fuzzy matching for the minisite grid search bar.
// Deliberately simple and predictable: case-insensitive, order-free token matching —
// every query token must appear as a substring of the listing's combined haystack
// (address, meta, price, notes, description, seller). No new deps.
import type { FavoriteListingsOfContact } from "../../../../../../@types/sharedSchema";
import type { MatchHit } from "./matchesSearch";
import { buildListingAddressLine, buildListingMetaLine } from "./matchesDisplay";

const normalize = (value: string | undefined | null): string =>
  (value ?? "").toString().toLowerCase().trim();

/** True when every whitespace-separated query token appears in the haystack. */
export function fuzzyMatches(query: string, haystack: string): boolean {
  const tokens = normalize(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const target = normalize(haystack);
  return tokens.every((token) => target.includes(token));
}

/** The searchable text for one minisite card: address / meta / notes / description. */
export function buildListingSearchHaystack(
  listing: MatchHit,
  favorites: FavoriteListingsOfContact | null | undefined
): string {
  const details = favorites?.additionalDetails?.[listing.objectID];
  return [
    buildListingAddressLine(listing),
    buildListingMetaLine(listing),
    listing.address?.city,
    listing.address?.neighborhood,
    listing.address?.street,
    listing.propertyType,
    listing.price != null ? String(listing.price) : "",
    details?.listingNotes,
    details?.descriptionOverride || listing.description,
    listing.sellerName,
    listing.agencyName
  ]
    .filter(Boolean)
    .join(" | ");
}
